import React, { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import authService, { authUserFromProfile } from '../../services/auth.service';
import { useAuthStore } from '../../stores/authStore';
import { dashboardPathForRole } from '../../lib/roleRouting';
import { getApiErrorMessage } from '../../lib/apiError';
import { mapPasswordError } from './changePassword.helpers';
import { PASSWORD_POLICY, policyPasses } from './passwordPolicy';
import { colors, gradients, shadows } from '../../styles/theme';

function RuleRow({ ok, children }: Readonly<{ ok: boolean; children: React.ReactNode }>): JSX.Element {
  return (
    <li style={{ color: ok ? colors.success : colors.danger }}>
      {ok ? '✓' : '✗'} {children}
    </li>
  );
}

interface PolicyChecklistProps {
  currentFilled: boolean;
  passes: boolean[];
  confirmsMatch: boolean;
  differsFromCurrent: boolean;
}

function PolicyChecklist({
  currentFilled,
  passes,
  confirmsMatch,
  differsFromCurrent,
}: Readonly<PolicyChecklistProps>): JSX.Element {
  return (
    <ul className="text-xs space-y-1 mt-2">
      <RuleRow ok={currentFilled}>Current password is required</RuleRow>
      {PASSWORD_POLICY.map((rule, i) => (
        <RuleRow key={rule.label} ok={passes[i]}>
          {rule.label}
        </RuleRow>
      ))}
      <RuleRow ok={confirmsMatch}>Confirmation matches</RuleRow>
      <RuleRow ok={differsFromCurrent}>Differs from the current password</RuleRow>
    </ul>
  );
}

/**
 * In-place password change.
 *
 * Two entry points:
 *   1. Forced — backend flagged `must_change_password=true` (bootstrap
 *      super_admin, accepted invitation, manager force-change). The
 *      ProtectedRoute guard pins the user here until they comply.
 *   2. Voluntary — from /profile (future).
 *
 * On success the auth store flag is cleared and the user is sent to
 * their role's dashboard. Other refresh tokens have already been
 * revoked server-side.
 */
const ChangePassword = (): JSX.Element => {
  const navigate = useNavigate();
  const { user, setUser } = useAuthStore();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorHint, setErrorHint] = useState<string | null>(null);

  const passes = useMemo(() => policyPasses(newPassword), [newPassword]);
  const samePolicyOk = passes.every(Boolean);
  const confirmsMatch = newPassword.length > 0 && newPassword === confirm;
  const differsFromCurrent = newPassword !== currentPassword;
  const formValid =
    currentPassword.length > 0 && samePolicyOk && confirmsMatch && differsFromCurrent;

  const isForced = Boolean(user?.must_change_password);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setErrorHint(null);
    if (!formValid) return;
    setLoading(true);
    try {
      const profile = await authService.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      // Refresh the user payload with the cleared `must_change_password`
      // flag so the route guard lets us through. Tokens are
      // untouched — `setUser` is profile-only.
      setUser(authUserFromProfile(profile));
      // If the account also owes a second-factor enrolment, the
      // ProtectedRoute guard pins it on /auth/mfa-enroll — route there
      // directly to avoid a dashboard flicker.
      const next = profile.must_enroll_mfa
        ? '/auth/mfa-enroll'
        : dashboardPathForRole(profile.role);
      navigate(next, { replace: true });
    } catch (err: unknown) {
      const raw = getApiErrorMessage(err, 'Could not change password.');
      const mapped = mapPasswordError(raw);
      setError(mapped.message);
      setErrorHint(mapped.hint ?? null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-8"
      style={{ background: gradients.navy }}
    >
      <div className="w-full max-w-md">
        <div
          className="rounded-2xl p-9"
          style={{ background: colors.surface, boxShadow: shadows.card }}
        >
          <h2 className="text-2xl font-bold mb-1" style={{ color: colors.ink }}>
            {isForced ? 'Set a new password' : 'Change password'}
          </h2>
          <p className="text-sm mb-7" style={{ color: colors.muted }}>
            {isForced
              ? 'Before continuing, choose a personal password to replace the bootstrap one. Other active sessions will be signed out.'
              : 'Choose a new password. Your other active sessions will be signed out.'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div
                className="p-3 rounded-lg text-sm"
                style={{
                  background: colors.dangerBg,
                  border: `1px solid ${colors.dangerBorder}`,
                  color: colors.danger,
                }}
                role="alert"
              >
                {error}
                {errorHint === 'forgot_password' && (
                  <span>
                    {' '}
                    <Link
                      to="/forgot-password"
                      style={{ color: colors.danger, textDecoration: 'underline' }}
                    >
                      Forgot your password?
                    </Link>
                  </span>
                )}
              </div>
            )}

            <div>
              <label
                htmlFor="change-current-password"
                className="block text-xs font-semibold mb-1.5 uppercase tracking-wide"
                style={{ color: colors.inkSoft }}
              >
                Current password
              </label>
              <input
                id="change-current-password"
                type={showPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                className="w-full px-4 py-2.5 text-sm rounded-xl outline-none transition-all"
                style={{
                  background: colors.canvas,
                  border: `1.5px solid ${colors.border}`,
                  color: colors.ink,
                }}
                required
                maxLength={128}
                disabled={loading}
                onFocus={(e) => (e.currentTarget.style.borderColor = colors.gold)}
                onBlur={(e) => (e.currentTarget.style.borderColor = colors.border)}
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label
                  htmlFor="change-new-password"
                  className="text-xs font-semibold uppercase tracking-wide"
                  style={{ color: colors.inkSoft }}
                >
                  New password
                </label>
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="text-xs"
                  style={{ color: colors.muted }}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              <input
                id="change-new-password"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                className="w-full px-4 py-2.5 text-sm rounded-xl outline-none transition-all"
                style={{
                  background: colors.canvas,
                  border: `1.5px solid ${colors.border}`,
                  color: colors.ink,
                }}
                required
                maxLength={128}
                disabled={loading}
                onFocus={(e) => (e.currentTarget.style.borderColor = colors.gold)}
                onBlur={(e) => (e.currentTarget.style.borderColor = colors.border)}
              />
            </div>

            <div>
              <label
                htmlFor="change-confirm-password"
                className="block text-xs font-semibold mb-1.5 uppercase tracking-wide"
                style={{ color: colors.inkSoft }}
              >
                Confirm new password
              </label>
              <input
                id="change-confirm-password"
                type={showPassword ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                className="w-full px-4 py-2.5 text-sm rounded-xl outline-none transition-all"
                style={{
                  background: colors.canvas,
                  border: `1.5px solid ${colors.border}`,
                  color: colors.ink,
                }}
                required
                maxLength={128}
                disabled={loading}
              />
            </div>

            <PolicyChecklist
              currentFilled={currentPassword.length > 0}
              passes={passes}
              confirmsMatch={confirmsMatch}
              differsFromCurrent={differsFromCurrent}
            />

            <button
              type="submit"
              disabled={loading || !formValid}
              className="w-full py-3 rounded-xl font-semibold text-sm transition-all mt-2"
              style={{
                background:
                  loading || !formValid
                    ? colors.disabledBg
                    : gradients.gold,
                color: loading || !formValid ? colors.disabledText : colors.navy,
                cursor: loading || !formValid ? 'not-allowed' : 'pointer',
                boxShadow: loading || !formValid ? 'none' : shadows.goldCta,
              }}
            >
              {loading ? 'Updating…' : 'Set new password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
